/**
 * Camera and QR decoding for the scanner kiosk.
 *
 * Decoding: the native `BarcodeDetector` where the browser has it; otherwise **jsQR 1.4.0** (pinned; pure JS, no WASM, so the
 * strict CSP needs nothing extra). The camera needs `getUserMedia`, which browsers allow on https or localhost only.
 */
import jsQR from "jsqr";
import { useEffect, useRef, useState, type RefObject } from "react";

export type Facing = "environment" | "user";
export type CameraProblem = "denied" | "none" | "unsupported" | "failed";

export const SCAN_EVERY_MS = 150;

/** Maps a `getUserMedia` failure to something the screen can explain. */
export function classifyCameraError(e: unknown): CameraProblem {
  const name = e instanceof DOMException || e instanceof Error ? e.name : "";
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "denied";
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") return "none";
  return "failed";
}

interface DetectorLike { detect(source: CanvasImageSource): Promise<{ rawValue: string }[]> }
type DetectorCtor = new (opts: { formats: string[] }) => DetectorLike;

export type Decoder = (video: HTMLVideoElement) => Promise<string | null>;

export interface DecoderDeps { BarcodeDetector?: DetectorCtor; jsqr?: typeof jsQR; canvas?: () => HTMLCanvasElement }

export function createDecoder(deps: DecoderDeps = {}): Decoder {
  const Native = deps.BarcodeDetector ?? (globalThis as { BarcodeDetector?: DetectorCtor }).BarcodeDetector;
  if (Native) {
    const detector = new Native({ formats: ["qr_code"] });
    return async (video) => (await detector.detect(video).catch(() => []))[0]?.rawValue ?? null;
  }
  const decode = deps.jsqr ?? jsQR;
  let canvas: HTMLCanvasElement | null = null;
  return async (video) => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;
    // A frame no bigger than needed: QR codes read fine at 640 wide and it keeps old tablets responsive.
    const scale = Math.min(1, 640 / w);
    const cw = Math.round(w * scale);
    const ch = Math.round(h * scale);
    canvas ??= (deps.canvas ?? (() => document.createElement("canvas")))();
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, cw, ch);
    const img = ctx.getImageData(0, 0, cw, ch);
    return decode(img.data, cw, ch, { inversionAttempts: "dontInvert" })?.data ?? null;
  };
}

export interface ScannerState { problem: CameraProblem | null; ready: boolean }

/**
 * Opens the camera into `video`, decodes about every 150 ms, and calls `onCode` for each read. `facing` flips on the switch
 * button. Tracks stop on unmount and when the facing changes.
 */
export function useCameraScanner(video: RefObject<HTMLVideoElement | null>, opts: { facing: Facing; enabled: boolean; onCode: (code: string) => void; decoder?: Decoder }): ScannerState {
  const [state, setState] = useState<ScannerState>({ problem: null, ready: false });
  const onCode = useRef(opts.onCode);
  onCode.current = opts.onCode;
  const { facing, enabled } = opts;
  const decoderOverride = opts.decoder;

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setState({ problem: null, ready: false });

    if (!navigator.mediaDevices?.getUserMedia) {
      setState({ problem: "unsupported", ready: false });
      return;
    }
    const decoder = decoderOverride ?? createDecoder();
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 720 } } });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        const el = video.current;
        if (!el) return;
        el.srcObject = stream;
        el.muted = true;
        await el.play().catch(() => undefined);
        setState({ problem: null, ready: true });
        const loop = async () => {
          if (stopped) return;
          try {
            const code = el.readyState >= 2 ? await decoder(el) : null;
            if (code && !stopped) onCode.current(code);
          } catch { /* a bad frame is not an error */ }
          if (!stopped) timer = setTimeout(() => void loop(), SCAN_EVERY_MS);
        };
        void loop();
      } catch (e) {
        if (!stopped) setState({ problem: classifyCameraError(e), ready: false });
      }
    })();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
      const el = video.current;
      if (el) el.srcObject = null;
    };
  }, [facing, enabled, decoderOverride, video]);

  return state;
}
